/**
 * CompanyInsightsTab Component
 *
 * Displays email activity insights for a company.
 * Shows total emails, key contacts, topics discussed, and relationship health.
 */

import { useTranslation } from 'react-i18next';
import type { CompanyEmailInsights } from '../types';

interface CompanyInsightsTabProps {
  insights: CompanyEmailInsights | null;
  loading: boolean;
  onNavigateToContact?: (contactId: string) => void;
}

export default function CompanyInsightsTab({
  insights,
  loading,
  onNavigateToContact,
}: CompanyInsightsTabProps) {
  const { t } = useTranslation('people');
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="text-center py-12 text-gray-500">
        <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <p className="font-medium">{t('company_insights_tab.empty_state.title')}</p>
        <p className="text-sm mt-1">{t('company_insights_tab.empty_state.description')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Email Activity */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h4 className="font-medium text-gray-900 flex items-center gap-2">
          <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          {t('email_insights_tab.email_activity')}
        </h4>
        <div className="mt-3 grid gap-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">{t('email_insights_tab.labels.total_emails')}</span>
            <span className="text-gray-900">{insights.total_emails}</span>
          </div>
          {insights.first_contact_at && (
            <div className="flex justify-between">
              <span className="text-gray-500">{t('email_insights_tab.labels.first_contact')}</span>
              <span className="text-gray-900">{new Date(insights.first_contact_at).toLocaleDateString()}</span>
            </div>
          )}
          {insights.last_contact_at && (
            <div className="flex justify-between">
              <span className="text-gray-500">{t('email_insights_tab.labels.last_contact')}</span>
              <span className="text-gray-900">{new Date(insights.last_contact_at).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </div>

      {/* Key Contacts */}
      {insights.key_contacts && insights.key_contacts.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
            <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            {t('company_insights_tab.key_contacts')}
          </h4>
          <div className="space-y-2">
            {insights.key_contacts.map((contact) => (
              <div
                key={contact.id}
                onClick={() => onNavigateToContact?.(contact.id)}
                className={`flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 ${
                  onNavigateToContact ? 'cursor-pointer' : ''
                }`}
              >
                <div>
                  <p className="font-medium text-gray-900">{contact.name}</p>
                  {contact.title && (
                    <p className="text-sm text-gray-500">{contact.title}</p>
                  )}
                </div>
                <span className="text-sm text-gray-500">{contact.email_count} emails</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Topics */}
      {insights.topics && insights.topics.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            {t('email_insights_tab.topics_discussed')}
          </h4>
          <div className="flex flex-wrap gap-2">
            {insights.topics.map((topic, i) => (
              <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded">
                {topic}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Relationship Health */}
      {insights.relationship_health !== undefined && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
            <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            {t('company_insights_tab.relationship_health')}
          </h4>
          <div className="flex items-center gap-4">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  insights.relationship_health >= 0.7
                    ? 'bg-green-500'
                    : insights.relationship_health >= 0.4
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
                }`}
                style={{ width: `${insights.relationship_health * 100}%` }}
              />
            </div>
            <span className="text-sm text-gray-600">
              {Math.round(insights.relationship_health * 100)}%
            </span>
          </div>
          {insights.sentiment_trend && (
            <p className="mt-2 text-sm text-gray-500">
              Trend: <span className={
                insights.sentiment_trend === 'improving' ? 'text-green-600' :
                insights.sentiment_trend === 'declining' ? 'text-red-600' :
                'text-gray-600'
              }>
                {insights.sentiment_trend}
              </span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
